import { useState, useEffect, useRef, useCallback } from 'react';
import { ApiClient } from '../services/api';
import type { TranscriptDownloadStatusResponse } from '../services/api';

export interface DownloadInfo {
  downloaded_at: string;
  download_origin?: string;
}

export interface OrgDownloadInfo extends DownloadInfo {
  user_email: string;
}

interface UseTranscriptDownloadStatusResult {
  myDownloads: Record<number, DownloadInfo>;
  orgDownloads: Record<number, OrgDownloadInfo>;
  loading: boolean;
  /** Optimistically mark transcript IDs as downloaded by me */
  markDownloaded: (ids: number[]) => void;
}

export function useTranscriptDownloadStatus(
  transcriptIds: number[],
  api: ApiClient,
  partnerId: string
): UseTranscriptDownloadStatusResult {
  const [myDownloads, setMyDownloads] = useState<Record<number, DownloadInfo>>({});
  const [orgDownloads, setOrgDownloads] = useState<Record<number, OrgDownloadInfo>>({});
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (transcriptIds.length === 0 || !partnerId) return;

    // Debounce to avoid rapid calls during filter/page changes
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const status: TranscriptDownloadStatusResponse = await api.getTranscriptDownloadStatus(
          partnerId,
          transcriptIds
        );
        setMyDownloads((prev) => ({ ...prev, ...status.my_downloads }));
        setOrgDownloads((prev) => ({ ...prev, ...status.org_downloads }));
      } catch (err) {
        console.error('[useTranscriptDownloadStatus] Failed to fetch status:', err);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => clearTimeout(debounceRef.current);
  }, [transcriptIds.join(','), partnerId]);

  const markDownloaded = useCallback((ids: number[]) => {
    const now = new Date().toISOString();
    setMyDownloads((prev) => {
      const updated = { ...prev };
      for (const id of ids) {
        updated[id] = { downloaded_at: now, download_origin: 'portal' };
      }
      return updated;
    });
  }, []);

  return { myDownloads, orgDownloads, loading, markDownloaded };
}
